import { sanitize } from "@/utils/formatUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { theme } from "antd";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { PageSizes } from "../constants";
import { FilterQueryKeys } from "../enums";
import type { EntityIdObject, SearchPageRequest, ThreeLevelEntityIdObject } from "../types";
import { resolvePageTitle } from "./navigationUtils";
import { URLQuery } from "./queryUtils";
import { numberOrZero } from "./utils";
import { regexPatterns } from "./validationUtils";

export const useCountdown = (): [number, (seconds: number) => void] => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (count <= 0) {
      return;
    }

    // One timeout per tick, so React cancels the pending one on unmount by itself.
    const timeoutId = setTimeout(() => setCount(count - 1), 1000);

    return () => clearTimeout(timeoutId);
  }, [count]);

  return [count, setCount];
};

export const useBackNavigation = (): VoidFunction => {
  const navigate = useNavigate();

  return () => {
    if (window.history.length <= 1) {
      navigate("..");
    } else {
      navigate(-1);
    }
  };
};

export const useUpdateFilterSearchParams = <T extends Record<string, any>>(defaultQueryParamsObject?: T) => {
  const navigate = useNavigate();

  return useCallback(
    (params: Partial<T>, append: boolean = false, replace: boolean = true) => {
      navigate(
        {
          search: URLQuery.searchParamsToString({
            searchParams: {
              ...params,
              ...(defaultQueryParamsObject ? skipInitialFormValues(params, defaultQueryParamsObject) : {}),
            },
            append,
          }),
        },
        { replace },
      );
    },
    [defaultQueryParamsObject, navigate],
  );
};

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState<{ innerWidth: number; outerWidth: number; height: number }>({
    innerWidth: window.innerWidth,
    outerWidth: window.outerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        innerWidth: window.innerWidth,
        outerWidth: window.outerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

export const useScrollToTopOnLoad = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
};

export const useDocumentTitle = () => {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    document.title = resolvePageTitle(pathname);
  }, [pathname]);
};

export const useAreParamsIdsValid = (): boolean => {
  const { id, id1, id2, id3 } = useParams<EntityIdObject & ThreeLevelEntityIdObject>();
  return (
    (!id || regexPatterns.exactUuidRegex.test(id)) &&
    (!id1 || regexPatterns.exactUuidRegex.test(id1)) &&
    (!id2 || regexPatterns.exactUuidRegex.test(id2)) &&
    (!id3 || regexPatterns.exactUuidRegex.test(id3))
  );
};

export const { useToken } = theme;

type FilterParams<T> = {
  params?: Partial<keyof (T & SearchPageRequest)>[];
  [FilterQueryKeys.PAGE_SIZE]?: number;
  defaultQueryParamsObject?: T;
};

type FilterSearchParams<T> = T & SearchPageRequest;

export const useFilterSearchParams = <T extends Record<string, any>>(
  options: FilterParams<T> = {},
): Partial<FilterSearchParams<T>> => {
  const defaultPageSize = options.defaultQueryParamsObject?.[FilterQueryKeys.PAGE_SIZE] ?? PageSizes.LARGE;
  const { params = [], pageSize = defaultPageSize, defaultQueryParamsObject } = options;
  const [searchParams] = useSearchParams();

  // Callers pass the options as inline literals, so the filter is rebuilt only when the URL changes; otherwise
  // its identity would change on every render and retrigger the effects and queries depending on it.
  return useMemo<Partial<FilterSearchParams<T>>>(
    () => ({
      [FilterQueryKeys.PAGE_INDEX]: numberOrZero(searchParams.get(FilterQueryKeys.PAGE_INDEX)),
      [FilterQueryKeys.PAGE_SIZE]: pageSize,
      [FilterQueryKeys.KEYWORD]: searchParams.get(FilterQueryKeys.KEYWORD) ?? undefined,
      ...URLQuery.getGenericQueryParams(searchParams, params, defaultQueryParamsObject),
    }),
    [searchParams],
  );
};

export const useFilterSpecificParams = <T extends Record<string, any>>(
  options: Partial<keyof T>[],
  defaultQueryParamsObject?: T,
): Partial<T> => {
  const [searchParams] = useSearchParams();

  // Rebuilt only when the URL changes, for the same reason as in useFilterSearchParams.
  return useMemo<Partial<T>>(
    () => URLQuery.getGenericQueryParams(searchParams, options, defaultQueryParamsObject),
    [searchParams],
  );
};

interface UseLocalSearchProps<T> {
  /** Data which are searched through. */
  data: T[];
  /** Searched keyword, comparison is done without accents and letter case. */
  keyword?: string;
  /** Resolves searchable values of the item, item matches when any of them contains the keyword. */
  getSearchedValues: (item: T) => (string | undefined)[];
  /** Simulated request delay in milliseconds. */
  delay?: number;
}

/**
 * Performs frontend search over the provided data. Hook simulates behavior of a react-query search hook,
 * therefore it returns filtered data together with the "isFetching" flag which is true while the simulated
 * request is in progress.
 */
export const useLocalSearch = <T>({ data, keyword, getSearchedValues, delay = 300 }: UseLocalSearchProps<T>) => {
  const [result, setResult] = useState<T[]>(data);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const sanitizedKeyword = keyword?.trim() ? sanitize(keyword) : undefined;

    if (!sanitizedKeyword) {
      setResult(data);
      setIsFetching(false);
      return;
    }

    setIsFetching(true);

    const timeout = setTimeout(() => {
      setResult(
        data.filter((item) =>
          getSearchedValues(item).some((value) => (value ? sanitize(value).includes(sanitizedKeyword) : false)),
        ),
      );
      setIsFetching(false);
    }, delay);

    return () => clearTimeout(timeout);
  }, [data, keyword, delay]);

  return { data: result, isFetching };
};

/**
 * An object URL holds the blob in memory until it is revoked, so it is created on subscribe and revoked on
 * unsubscribe — unlike a URL created during render, this also survives StrictMode's double mount.
 */
const createObjectUrlStore = (blob?: Blob) => {
  let url: string | undefined;

  return {
    subscribe: (onStoreChange: VoidFunction) => {
      if (!blob) {
        return () => {};
      }

      const objectUrl = URL.createObjectURL(blob);
      url = objectUrl;
      onStoreChange();

      return () => {
        URL.revokeObjectURL(objectUrl);
        url = undefined;
      };
    },
    getSnapshot: () => url,
  };
};

export const useObjectUrl = (blob?: Blob) => {
  const store = useMemo(() => createObjectUrlStore(blob), [blob]);

  return useSyncExternalStore(store.subscribe, store.getSnapshot);
};
